// Detail produktu v Analýza → Do košíku: týdenní poměr přidání do košíku
// + poznámky ke změnám na detailu a srovnání 4 týdnů před / po změně.

import {
  type WeekPoint,
  WeeklyRateChart,
  fmtDen,
  fmtPct,
  mondayOf,
} from '@/components/analytics/WeeklyRateChart';
import { useEscapeKey } from '@/hooks/useEscapeKey';
import { ApiError, apiFetch } from '@/lib/apiFetch';
import { useCallback, useEffect, useState } from 'react';

type Series = { total: WeekPoint[]; desktop: WeekPoint[]; mobile: WeekPoint[] };
type Note = {
  id: number;
  note_date: string;
  note: string;
  color: string;
  created_by: string | null;
};
export type TrendProduct = { code: string; name: string; image_url: string | null };

const COLORS = ['#ef4444', '#f59e0b', '#10b981', '#8b5cf6', '#ec4899'];

/** Vážený poměr (součet přidání ÷ součet zobrazení) přes vybrané týdny. */
function weighted(points: WeekPoint[]): { rate: number | null; views: number } {
  const v = points.reduce((s, p) => s + p.views, 0);
  const a = points.reduce((s, p) => s + p.adds, 0);
  return { rate: v > 0 ? (a / v) * 100 : null, views: v };
}

/** 4 celé týdny před týdnem změny vs. 4 týdny od týdne po změně (týden změny se vynechá). */
function beforeAfter(series: WeekPoint[], noteDate: string) {
  const i = series.findIndex((p) => p.start === mondayOf(noteDate));
  if (i < 0) return null;
  return {
    before: weighted(series.slice(Math.max(0, i - 4), i)),
    after: weighted(series.slice(i + 1, i + 5)),
  };
}

export function CartRateTrendModal({
  product,
  eshop,
  onClose,
}: {
  product: TrendProduct;
  eshop: 'cz' | 'sk' | 'all';
  onClose: () => void;
}) {
  useEscapeKey(onClose);
  const [weeks, setWeeks] = useState(26);
  const [series, setSeries] = useState<Series | null>(null);
  const [notes, setNotes] = useState<Note[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), text: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let zruseno = false;
    setSeries(null);
    setErr(null);
    const params = new URLSearchParams({ code: product.code, weeks: String(weeks), eshop });
    apiFetch<{ series: Series }>(`/api/analytics/cart-rate/trend?${params.toString()}`)
      .then((r) => {
        if (!zruseno) setSeries(r.series);
      })
      .catch((e: unknown) => {
        if (!zruseno) setErr(e instanceof ApiError ? e.describe() : 'Načtení trendu selhalo');
      });
    return () => {
      zruseno = true;
    };
  }, [product.code, weeks, eshop]);

  const loadNotes = useCallback(async () => {
    try {
      const r = await apiFetch<{ notes: Note[] }>(
        `/api/analytics/cart-rate/notes?code=${encodeURIComponent(product.code)}`,
      );
      setNotes(r.notes);
    } catch {
      // poznámky nejsou kritické — graf funguje i bez nich
    }
  }, [product.code]);

  useEffect(() => {
    void loadNotes();
  }, [loadNotes]);

  async function addNote() {
    if (!form.text.trim()) return;
    setSaving(true);
    try {
      await apiFetch('/api/analytics/cart-rate/notes', {
        method: 'POST',
        body: {
          code: product.code,
          note_date: form.date,
          note: form.text.trim(),
          color: COLORS[notes.length % COLORS.length],
        },
      });
      setForm((f) => ({ ...f, text: '' }));
      await loadNotes();
    } catch (e: unknown) {
      setErr(e instanceof ApiError ? e.describe() : 'Uložení poznámky selhalo');
    } finally {
      setSaving(false);
    }
  }

  async function deleteNote(id: number) {
    try {
      await apiFetch(`/api/analytics/cart-rate/notes/${id}`, { method: 'DELETE' });
      await loadNotes();
    } catch (e: unknown) {
      setErr(e instanceof ApiError ? e.describe() : 'Smazání selhalo');
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto">
      <button
        type="button"
        aria-label="Zavřít"
        onClick={onClose}
        className="fixed inset-0 bg-black/40 cursor-default"
      />
      <div className="relative w-full max-w-4xl my-8 bg-surface rounded-2xl shadow-modal p-6">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex items-center gap-3 min-w-0">
            {product.image_url && (
              <img
                src={product.image_url}
                alt=""
                className="h-12 w-12 shrink-0 object-contain rounded border border-border bg-white"
              />
            )}
            <div className="min-w-0">
              <h2 className="text-lg font-bold text-text truncate">{product.name}</h2>
              <p className="text-xs text-text-muted font-mono">
                {product.code} · {eshop === 'all' ? 'CZ + SK' : eshop.toUpperCase()}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {[12, 26, 52].map((w) => (
              <button
                key={w}
                type="button"
                onClick={() => setWeeks(w)}
                className={`text-xs px-3 py-1.5 rounded-lg border cursor-pointer ${
                  weeks === w
                    ? 'bg-primary text-white border-primary'
                    : 'border-border text-text-secondary hover:bg-surface-hover'
                }`}
              >
                {w} týdnů
              </button>
            ))}
            <button
              type="button"
              onClick={onClose}
              aria-label="Zavřít"
              className="ml-2 text-text-muted hover:text-text text-xl leading-none cursor-pointer"
            >
              ✕
            </button>
          </div>
        </div>

        {err && <p className="text-sm text-danger mb-3">{err}</p>}
        {!series && !err && <p className="text-sm text-text-muted py-16 text-center">Načítám…</p>}

        {series && (
          <WeeklyRateChart
            lines={[
              { label: 'Celkem', color: '#94a3b8', points: series.total, muted: true },
              { label: 'PC', color: '#3b82f6', points: series.desktop },
              { label: 'Mobil (vč. tabletů)', color: '#f97316', points: series.mobile },
            ]}
            markers={notes.map((n) => ({ id: n.id, date: n.note_date, color: n.color }))}
          />
        )}

        <h3 className="text-sm font-bold text-text mt-6 mb-2">Změny na detailu produktu</h3>
        {notes.length === 0 && (
          <p className="text-xs text-text-muted mb-2">
            Zatím žádná. Zapiš, co jste změnili (např. nové fotky) — v grafu se objeví čárou a
            uvidíš poměr 4 týdny před a po.
          </p>
        )}
        <div className="space-y-2 mb-3">
          {notes.map((n) => {
            const ba = series ? beforeAfter(series.total, n.note_date) : null;
            const diff =
              ba?.before.rate != null && ba.after.rate != null
                ? ba.after.rate - ba.before.rate
                : null;
            return (
              <div
                key={n.id}
                className="flex items-center gap-3 text-sm border border-border-light rounded-lg px-3 py-2"
              >
                <svg viewBox="0 0 12 12" className="h-3 w-3 shrink-0" aria-hidden="true">
                  <circle cx="6" cy="6" r="6" fill={n.color} />
                </svg>
                <span className="text-text-secondary whitespace-nowrap">{fmtDen(n.note_date)}</span>
                <span className="flex-1 min-w-0 text-text">{n.note}</span>
                {ba && (
                  <span className="text-xs text-text-secondary whitespace-nowrap">
                    {fmtPct(ba.before.rate)} → {fmtPct(ba.after.rate)}
                    {diff != null && (
                      <strong className={`ml-1 ${diff >= 0 ? 'text-success' : 'text-danger'}`}>
                        ({diff >= 0 ? '+' : ''}
                        {diff.toFixed(1).replace('.', ',')} b.)
                      </strong>
                    )}
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => void deleteNote(n.id)}
                  className="text-text-muted hover:text-danger cursor-pointer"
                  aria-label="Smazat poznámku"
                >
                  ✕
                </button>
              </div>
            );
          })}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="date"
            value={form.date}
            onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
            className="border border-border rounded-lg px-3 py-1.5 text-sm text-text"
          />
          <input
            type="text"
            value={form.text}
            maxLength={300}
            onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void addNote();
            }}
            placeholder="Co se změnilo (např. nové fotky, tabulka velikostí výš)"
            className="flex-1 min-w-[240px] border border-border rounded-lg px-3 py-1.5 text-sm text-text"
          />
          <button
            type="button"
            onClick={() => void addNote()}
            disabled={saving || !form.text.trim()}
            className="rounded-lg bg-primary px-4 py-1.5 text-sm font-semibold text-white disabled:opacity-50 cursor-pointer"
          >
            Přidat
          </button>
        </div>
        <p className="text-[11px] text-text-muted mt-2">
          Srovnání: 4 celé týdny před týdnem změny vs. 4 týdny po něm (týden změny se nepočítá).
        </p>
      </div>
    </div>
  );
}
